import { SeoConfig } from "../types";

/**
 * Known city geo-coordinates for precise local search & AI overview citation (GEO).
 */
const CITY_GEO_DATA: Record<string, { region: string; placename: string; position: string; icbm: string }> = {
  bhopal: {
    region: "IN-MP",
    placename: "Bhopal, Madhya Pradesh, India",
    position: "23.2599;77.4126",
    icbm: "23.2599, 77.4126",
  },
  sehore: {
    region: "IN-MP",
    placename: "Sehore, Madhya Pradesh, India",
    position: "23.2032;77.0845",
    icbm: "23.2032, 77.0845",
  },
  indore: {
    region: "IN-MP",
    placename: "Indore, Madhya Pradesh, India",
    position: "22.7196;75.8577",
    icbm: "22.7196, 75.8577",
  },
};

/**
 * Dynamically updates document title, meta description, keywords, Open Graph,
 * GEO meta tags (local search & generative AI search grounding), and Schema.org JSON-LD structured data tag in <head>.
 */
export function updateDynamicSeoHead(config: Partial<SeoConfig>) {
  if (typeof window === "undefined") return;

  const { city, categoryName, metaTitle, metaDescription, keywords, schemaJsonLd } = config;

  // 1. Update Document Title
  if (metaTitle) {
    document.title = metaTitle;
  } else if (city) {
    const formattedCity = city.charAt(0).toUpperCase() + city.slice(1);
    document.title = `RentLow - ${categoryName || 'Appliance'} Rentals in ${formattedCity} @ Zero Deposit | Free Delivery`;
  }

  // Helper to set or create meta element
  const setMetaTag = (nameOrProperty: "name" | "property", key: string, content: string) => {
    let element = document.querySelector(`meta[${nameOrProperty}="${key}"]`) as HTMLMetaElement;
    if (!element) {
      element = document.createElement("meta");
      element.setAttribute(nameOrProperty, key);
      document.head.appendChild(element);
    }
    element.setAttribute("content", content);
  };

  // 2. Meta Description & Keywords
  if (metaDescription) {
    setMetaTag("name", "description", metaDescription);
    setMetaTag("property", "og:description", metaDescription);
    setMetaTag("name", "twitter:description", metaDescription);
  }

  if (metaTitle) {
    setMetaTag("property", "og:title", metaTitle);
    setMetaTag("name", "twitter:title", metaTitle);
  }

  setMetaTag("property", "og:locale", "en_IN");
  setMetaTag("property", "og:site_name", "RentLow");

  if (keywords && Array.isArray(keywords) && keywords.length > 0) {
    setMetaTag("name", "keywords", keywords.join(", "));
  } else if (city) {
    const cityName = city.toLowerCase();
    setMetaTag("name", "keywords", `${categoryName || 'appliance'} on rent ${cityName}, AC rental ${cityName}, fridge on rent ${cityName}, washing machine rental ${cityName}, furniture on rent ${cityName}, rentlow ${cityName}`);
  }

  // 3. Update GEO Meta Tags for Local Search & AI Generative Search Engines (GEO)
  const currentCityKey = (city || "bhopal").toLowerCase();
  const geoInfo = CITY_GEO_DATA[currentCityKey] || CITY_GEO_DATA["bhopal"];

  setMetaTag("name", "geo.region", geoInfo.region);
  setMetaTag("name", "geo.placename", geoInfo.placename);
  setMetaTag("name", "geo.position", geoInfo.position);
  setMetaTag("name", "ICBM", geoInfo.icbm);

  // 4. Update Canonical Link
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", window.location.origin + window.location.pathname + window.location.search);

  // 5. Inject / Update Schema.org JSON-LD
  if (schemaJsonLd) {
    let schemaScript = document.getElementById("rentlow-jsonld-schema") as HTMLScriptElement;
    if (!schemaScript) {
      schemaScript = document.createElement("script");
      schemaScript.id = "rentlow-jsonld-schema";
      schemaScript.type = "application/ld+json";
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = typeof schemaJsonLd === "string" ? schemaJsonLd : JSON.stringify(schemaJsonLd);
  }
}

